import React, { useState, useEffect, useCallback, useRef } from 'react';
import { B } from '../../constants';
import { borderRadius, shadows, transitions } from '../../designTokens';

const variants = {
  primary: {
    background: B.forest,
    color: 'white',
    border: 'none',
    boxShadow: `0 4px 14px rgba(26,107,74,0.35)`,
    hoverBg: '#14553a',
  },
  secondary: {
    background: 'white',
    color: B.night,
    border: `1.5px solid #e5e0d8`,
    boxShadow: shadows.sm,
    hoverBg: '#f9f9f9',
  },
  tertiary: {
    background: '#f5f5f5',
    color: B.night,
    border: 'none',
    boxShadow: 'none',
    hoverBg: '#ebebeb',
  },
  ghost: {
    background: 'transparent',
    color: B.muted,
    border: 'none',
    boxShadow: 'none',
    hoverBg: 'rgba(0,0,0,0.04)',
  },
  accent: {
    background: `linear-gradient(135deg, ${B.forest}, ${B.mint})`,
    color: 'white',
    border: 'none',
    boxShadow: `0 4px 14px rgba(26,107,74,0.35)`,
    hoverBg: B.forest,
  },
  destructive: {
    background: B.error,
    color: 'white',
    border: 'none',
    boxShadow: shadows.sm,
    hoverBg: '#dc2626',
  },
  link: {
    background: 'transparent',
    color: B.forest,
    border: 'none',
    boxShadow: 'none',
    hoverBg: 'transparent',
  },
  outline: {
    background: 'transparent',
    color: B.forest,
    border: `2px solid ${B.forest}`,
    boxShadow: 'none',
    hoverBg: 'rgba(26,107,74,0.04)',
  },
};

const sizeMap = {
  xs: { padding: '4px 10px', fontSize: 11 },
  sm: { padding: '6px 14px', fontSize: 12 },
  md: { padding: '10px 22px', fontSize: 13 },
  lg: { padding: '14px 32px', fontSize: 14 },
  xl: { padding: '18px 38px', fontSize: 16 },
};

const iconOnlySizeMap = {
  xs: { padding: '4px', width: 26, height: 26, fontSize: 11 },
  sm: { padding: '6px', width: 30, height: 30, fontSize: 12 },
  md: { padding: '10px', width: 36, height: 36, fontSize: 13 },
  lg: { padding: '14px', width: 44, height: 44, fontSize: 14 },
  xl: { padding: '18px', width: 54, height: 54, fontSize: 16 },
};

const radiusMap = {
  sm: borderRadius.sm,
  md: borderRadius.md,
  lg: borderRadius.lg,
  pill: borderRadius.pill,
};

function Spinner({ color, size = 14 }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        width: size,
        height: size,
        border: `2px solid ${color}33`,
        borderTopColor: color,
        borderRadius: '50%',
        animation: 'spin 0.6s linear infinite',
        flexShrink: 0,
      }}
    />
  );
}

let rippleStylesInjected = false;

function ensureRippleStyles() {
  if (typeof document === 'undefined' || rippleStylesInjected) return;
  const style = document.createElement('style');
  style.textContent = `@keyframes ripple{to{transform:scale(4);opacity:0}}`;
  document.head.appendChild(style);
  rippleStylesInjected = true;
}

function RippleEffect({ ripples }) {
  return ripples.map((r) => (
    <span
      key={r.id}
      style={{
        position: 'absolute',
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.35)',
        width: r.size,
        height: r.size,
        left: r.x - r.size / 2,
        top: r.y - r.size / 2,
        transform: 'scale(0)',
        animation: 'ripple 0.6s ease-out',
        pointerEvents: 'none',
      }}
    />
  ));
}

export default function Button({
  as: Tag = 'button',
  variant = 'primary',
  size = 'md',
  radius = 'pill',
  children,
  onClick,
  disabled,
  loading,
  loadingPosition = 'left',
  loadingVariant = 'spinner',
  loadingText,
  transitionDuration = 'normal',
  style,
  type,
  leftIcon,
  rightIcon,
  fullWidth,
  iconOnly,
  focusRingColor,
  ripple,
  ...props
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [ripples, setRipples] = useState([]);
  const [reducedMotion, setReducedMotion] = useState(false);
  const btnRef = useRef(null);
  const rippleIdRef = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (ripple) ensureRippleStyles();
  }, [ripple]);

  const v = variants[variant] || variants.primary;
  const isLink = variant === 'link';
  const activeSize = iconOnly
    ? iconOnlySizeMap[size] || iconOnlySizeMap.md
    : sizeMap[size] || sizeMap.md;
  const radiusValue = radiusMap[radius] || radiusMap.pill;
  const spinnerColor = v.color === 'white' ? 'white' : B.forest;
  const isDisabled = disabled || loading;
  const transitionValue = transitions[transitionDuration] || transitionDuration || transitions.normal;

  const addRipple = useCallback((e) => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 1.5;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = ++rippleIdRef.current;
    setRipples((prev) => [...prev, { x, y, size, id }]);
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== id));
    }, 600);
  }, []);

  const handleClick = useCallback((e) => {
    if (isDisabled) return;
    onClick?.(e);
  }, [isDisabled, onClick]);

  const handleMouseDown = useCallback((e) => {
    if (isDisabled) return;
    if (ripple) addRipple(e);
    if (!isLink) e.currentTarget.style.transform = 'scale(0.97)';
  }, [isDisabled, ripple, addRipple, isLink]);

  const handleMouseEnter = useCallback((e) => {
    if (isDisabled) return;
    if (isLink) {
      e.currentTarget.style.textDecoration = 'underline';
    } else {
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.boxShadow = shadows.elevated;
    }
  }, [isDisabled, isLink]);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = v.boxShadow || 'none';
    if (isLink) e.currentTarget.style.textDecoration = 'none';
  }, [v.boxShadow, isLink]);

  const handleMouseUp = useCallback((e) => {
    if (isDisabled || isLink) return;
    e.currentTarget.style.transform = 'translateY(-1px)';
  }, [isDisabled, isLink]);

  const focusRingStyle = isFocused && !isDisabled ? {
    outline: 'none',
    boxShadow: `0 0 0 2px ${focusRingColor || (variant === 'destructive' ? B.error : B.forest)}`,
    transition: reducedMotion ? 'none' : undefined,
  } : {
    outline: 'none',
  };

  const renderContent = () => {
    if (loading && loadingVariant === 'skeleton') {
      return (
        <span
          style={{
            display: 'block',
            width: '60%',
            height: 10,
            borderRadius: borderRadius.xs,
            background: 'rgba(255,255,255,0.25)',
            animation: reducedMotion ? 'none' : 'pulse 1.5s ease-in-out infinite',
          }}
        />
      );
    }

    if (loading && loadingPosition === 'center') {
      return <Spinner color={spinnerColor} />;
    }

    if (iconOnly) {
      return leftIcon || rightIcon || children;
    }

    const showIcons = !loading || loadingText;
    const label = loading && loadingText ? loadingText : children;

    return (
      <>
        {loading && loadingPosition === 'left' && <Spinner color={spinnerColor} />}
        {leftIcon && showIcons && <span style={{ display: 'inline-flex', flexShrink: 0 }}>{leftIcon}</span>}
        {label}
        {rightIcon && showIcons && <span style={{ display: 'inline-flex', flexShrink: 0 }}>{rightIcon}</span>}
        {loading && loadingPosition === 'right' && <Spinner color={spinnerColor} />}
      </>
    );
  };

  const isButton = Tag === 'button';
  const elementProps = {
    ref: btnRef,
    onClick: handleClick,
    style: {
      fontFamily: "'Montserrat',sans-serif",
      fontWeight: 700,
      borderRadius: radiusValue,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      opacity: isDisabled ? 0.5 : 1,
      transition: reducedMotion ? 'none' : `all ${transitionValue}`,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: iconOnly ? 0 : 8,
      position: 'relative',
      overflow: 'hidden',
      width: fullWidth ? '100%' : iconOnly ? activeSize.width : undefined,
      height: iconOnly ? activeSize.height : undefined,
      padding: activeSize.padding,
      fontSize: activeSize.fontSize,
      background: v.background,
      color: v.color,
      border: v.border,
      boxShadow: v.boxShadow,
      textDecoration: isLink ? 'none' : undefined,
      textUnderlineOffset: isLink ? 3 : undefined,
      whiteSpace: 'nowrap',
      lineHeight: 1.2,
      ...focusRingStyle,
      ...style,
    },
    ...props,
  };

  if (isButton) {
    elementProps.disabled = isDisabled;
    elementProps.type = type;
    elementProps.onFocus = () => setIsFocused(true);
    elementProps.onBlur = () => setIsFocused(false);
    elementProps.onMouseEnter = handleMouseEnter;
    elementProps.onMouseLeave = handleMouseLeave;
    elementProps.onMouseDown = handleMouseDown;
    elementProps.onMouseUp = handleMouseUp;
  }

  return React.createElement(
    Tag,
    elementProps,
    renderContent(),
    ripple && React.createElement(RippleEffect, { ripples }),
  );
}
